import { useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import UPLoadingSpinner from 'components/loading-spinner';
import localStorageService from 'services/local-storage-service';
import { useAppDispatch } from 'store';
import { setAuthTokenAction } from 'store/actions/auth-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { MFA_ENROLLMENT__BACK_TO_SIGN_IN } from 'translations/constants';
import IToken from 'types/auth/IToken';
import { MfaCard, MfaPageContainer, MfaRightColumn } from '../mfa-styles';
import { AuthLeftContainer } from '../styles';
import MfaBackupCodesView from './MfaBackupCodesView';
import MfaSetupView from './MfaSetupView';
import useMfaEnrollment from './useMfaEnrollment';

type MfaStep = 'setup' | 'codes';

/**
 * First-time TOTP enrollment. Two screens: setup (QR, secret, 6-digit code) then backup codes.
 *
 * The mfaToken arrives in `location.state` and lives only in memory — it is not a session token and
 * must never reach Redux, localStorage or a URL. A refresh empties `location.state`, which is why the
 * guard below sends the user back to login rather than trying to recover: the token is 15-minute,
 * single-use, and the next login issues a fresh one.
 *
 * Note that Continue on the *setup* screen is what commits the enrollment — there is no
 * validate-only endpoint. By the time the backup codes render, the enrollment is already permanent.
 * See section 7 of the design doc for what that costs.
 */
const MfaEnrollment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<MfaStep>('setup');
  const [session, setSession] = useState<IToken>(null);
  const mfaToken = (location.state as { mfaToken?: string })?.mfaToken;

  const goToLoginWithMessage = useCallback(
    (messageKey: string) => {
      navigate('/auth/login', { replace: true, state: { mfaErrorKey: messageKey } });
    },
    [navigate],
  );

  const { payload, loading, submitting, failure, retrySeconds, confirm, clearFailure } = useMfaEnrollment(
    mfaToken,
    goToLoginWithMessage,
  );

  const onSubmitCode = useCallback(
    async (code: string) => {
      const token = await confirm(code);
      if (token) {
        setSession(token);
        setStep('codes');
      }
    },
    [confirm],
  );

  const onBackToLogin = useCallback(() => navigate('/auth/login', { replace: true }), [navigate]);

  // Persisting the session is the last thing that happens: it flips authStateSelector, which swaps
  // Router from AuthRouter to AppRouter. AppRouter's catch-all then resolves /auth/mfa-enrollment to
  // /dashboard, so no explicit navigate is needed — this mirrors what an ordinary login does.
  const onConfirmCodesStored = useCallback(() => {
    localStorageService.setItem({ authToken: session.token });
    dispatch(setAuthTokenAction(session.token));
    dispatch(setMenuStatusAction('Dashboard'));
  }, [dispatch, session]);

  if (!mfaToken) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <MfaPageContainer>
      <AuthLeftContainer />
      <MfaRightColumn>
        <MfaCard>
          {loading && <UPLoadingSpinner />}
          {!loading && payload && step === 'setup' && (
            <MfaSetupView
              payload={payload}
              submitting={submitting}
              failure={failure}
              retrySeconds={retrySeconds}
              onSubmit={onSubmitCode}
              onCancel={onBackToLogin}
              cancelLabelKey={MFA_ENROLLMENT__BACK_TO_SIGN_IN}
              onCodeEdited={clearFailure}
            />
          )}
          {!loading && payload && step === 'codes' && (
            <MfaBackupCodesView codes={payload.backupCodes} onConfirm={onConfirmCodesStored} />
          )}
        </MfaCard>
      </MfaRightColumn>
    </MfaPageContainer>
  );
};

export default MfaEnrollment;
